<template>
    <div class="add-on-settings-container">
        <!-- Initial Loading State -->
        <div v-if="isInitialLoading" class="text-center py-4">
            <font-awesome-icon icon="spinner" class="spinner-icon" spin />
            <p class="mt-2 text-muted">Loading add-ons...</p>
        </div>

        <!-- Database Not Configured State -->
        <div v-else-if="databaseNotConfigured" class="database-error-state">
            <div class="error-icon-wrapper">
                <font-awesome-icon icon="exclamation-triangle" class="error-icon" />
            </div>
            <h5 class="error-title">Database Not Configured</h5>
            <p class="error-message">
                The database for this organization has not been configured yet. 
                Please contact your system administrator to complete the setup before managing add-ons.
            </p>
            <div class="error-details">
                <small class="text-muted">
                    <font-awesome-icon icon="info-circle" class="mr-1" />
                    Organization setup is still in progress
                </small>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="addons.length === 0" class="text-center py-4">
            <p class="text-muted">No add-ons available for this organization.</p>
        </div>

        <!-- Add-ons List -->
        <div v-else>
        <div class="form-group row" v-for="(addon, index) in addons" :key="addon.id">
            <div class="col-md-10 text-left">
                    <div class="d-flex align-items-center">
                        <p class="add-on-keys mb-0">{{addon.label}}</p>
                        <!-- Loading Spinner next to addon name -->
                        <font-awesome-icon
                            v-if="loadingAddons[addon.id]"
                            icon="spinner"
                            class="spinner-icon-inline ml-2"
                            spin
                        />
                    </div>
                    <small v-if="addon.label && addon.label.toLowerCase().includes('power bi') && loadingAddons[addon.id]" class="text-info d-block mt-1">
                       Please Wait...
                    </small>

                    <!-- AI Assistant plan picker, shown inline once the addon is enabled -->
                    <div v-if="isAiAssistant(addon) && addon.is_checked" class="plan-section mt-3">
                        <h6 class="plan-section-title">Plan</h6>
                        <div v-if="isLoadingPlans" class="text-muted">
                            <font-awesome-icon icon="spinner" spin /> Loading plans...
                        </div>
                        <div v-else-if="plans.length === 0" class="text-muted">
                            No AI plans are configured yet.
                        </div>
                        <div v-else>
                            <select
                                class="form-control"
                                v-model="selectedPlanId"
                                :disabled="isSavingPlan"
                                @change="savePlan"
                            >
                                <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                                    {{ planLabel(plan) }}
                                </option>
                            </select>
                            <small v-if="isSavingPlan" class="text-info d-block mt-2">
                                <font-awesome-icon icon="spinner" spin /> Saving...
                            </small>
                            <small v-if="subscriptionPeriod" class="text-muted d-block mt-2">
                                Current period: {{ subscriptionPeriod }}
                            </small>
                        </div>
                    </div>
                </div>
                <div class="col-md-2 text-right">
                    <!-- Toggle Button -->
                    <toggle-button
                        :key="`toggle-${addon.id}-${addon.renderKey || 0}`"
                        @change="saveAddons(addon.id, $event, index)"
                        :value="!!addon.is_checked"
                        :disabled="loadingAddons[addon.id] || isSaving"
                        :class="{ 'toggle-disabled': loadingAddons[addon.id] || isSaving }"
                    />
                </div>
            </div>
        </div>

        <!-- Power BI Disable Confirmation Modal -->
        <div v-if="showPowerBIModal" class="powerbi-modal-overlay" @click="closePowerBIModal">
            <div class="powerbi-modal-content" @click.stop>
                <div class="powerbi-modal-header">
                    <h4>Disable Power BI Integration</h4>
                    <button class="close-btn" @click="closePowerBIModal">
                        <font-awesome-icon icon="times" />
                    </button>
                </div>
                <div class="powerbi-modal-body">
                    <p class="modal-message">What would you like to do with your Power BI data?</p>
                    <div class="modal-actions">
                        <button class="action-btn keep-data-btn" @click="handleKeepData">
                            <font-awesome-icon icon="database" class="mr-2" />
                            Keep Data
                        </button>
                        <button class="action-btn discard-data-btn" @click="handleDiscardData">
                            <font-awesome-icon icon="trash-alt" class="mr-2" />
                            Discard Data
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import axios from 'axios';
    import moment from 'moment';
    import ToggleButton from 'vue-js-toggle-button';
    Vue.use(ToggleButton)

    export default {
        props:["formData"],
        data() {
            return {
                org_id: '',
                addons: [],
                isInitialLoading: true,
                databaseNotConfigured: false,
                loadingAddons: {},
                isSaving: false,
                requestTimeouts: {},
                previousStates: {},
                showPowerBIModal: false,
                pendingPowerBIAction: null,
                // AI Assistant plan state (single addon, so flat state is fine)
                plans: [],
                selectedPlanId: null,
                // Last plan id the server actually accepted, so a failed save
                // can put the select back instead of showing an unsaved value.
                lastSavedPlanId: null,
                currentSubscription: null,
                isLoadingPlans: false,
                isSavingPlan: false,
            };
        },

        computed: {
            /**
             * Billing period line. Empty unless both ends parse, so a missing
             * or zero date renders nothing rather than "Invalid date".
             */
            subscriptionPeriod() {
                if (!this.currentSubscription) return '';

                const start = this.formatDate(this.currentSubscription.current_period_start);
                const end = this.formatDate(this.currentSubscription.current_period_end);
                if (!start || !end) return '';

                return `${start} – ${end}`;
            }
        },

        created() {
            this.fetchAddons();
        },

        watch: {
            'formData.org_id'(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    this.databaseNotConfigured = false;
                    this.resetPlanState();
                    this.fetchAddons();
                }
            }
        },
       
        methods: {
            /**
             * Fetch add-ons for the organization
             */
            fetchAddons() {
                if (!this.formData || !this.formData.org_id) {
                    this.isInitialLoading = false;
                    return;
                }

                this.isInitialLoading = true;
                this.org_id = this.formData.org_id;

                axios.get(`admin/organization/addons/${this.org_id}`)
                    .then((response) => {
                        const res = response.data.data;
                        if (res && Array.isArray(res)) {
                            this.addons = res;
                            this.addons.forEach(addon => {
                                this.$set(this.loadingAddons, addon.id, false);
                                this.$set(addon, 'renderKey', 0);
                            });
                            this.databaseNotConfigured = false;

                            // If the AI Assistant addon is already enabled, load its plans
                            const aiAddon = this.addons.find(a => this.isAiAssistant(a));
                            if (aiAddon && aiAddon.is_checked) {
                                this.fetchPlans();
                            } else {
                                this.resetPlanState();
                            }
                        } else {
                            this.addons = [];
                            this.$toast.warning("No add-ons found for this organization.");
                        }
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.error('Error fetching add-ons:', error);
                        this.addons = [];
                        
                        // Check for database configuration errors
                        const errorCode = error.response && error.response.data && error.response.data.error;
                        if (errorCode === 'database_not_configured' || errorCode === 'database_not_accessible') {
                            this.databaseNotConfigured = true;
                            // Don't show toast for database errors as we show a dedicated UI
                            return;
                        }
                        
                        const errorMessage = (error.response && error.response.data && error.response.data.message) 
                            || "Failed to load add-ons. Please try again.";
                        this.$toast.error(errorMessage);
                    })
                    .finally(() => {
                        this.isInitialLoading = false;
                    });
            },

            /**
             * Revert toggle to previous state and force re-render
             */
            revertToggle(addon_id) {
                const addon = this.addons.find(a => a.id === addon_id);
                if (addon && this.previousStates[addon_id] !== undefined) {
                    this.$set(addon, 'is_checked', this.previousStates[addon_id]);
                    this.$set(addon, 'renderKey', (addon.renderKey || 0) + 1);
                }
            },

            /**
             * Get user-friendly error message
             */
            getErrorMessage(error) {
                if (error.response) {
                    if (error.response.status === 408 || error.code === 'ECONNABORTED') {
                        return "Request timed out. Please try again.";
                    }
                    if (error.response.data && error.response.data.message) {
                        return error.response.data.message;
                    }
                    if (error.response.status === 500) {
                        return "Server error. Please contact support if the issue persists.";
                    }
                    if (error.response.status === 403) {
                        return "You don't have permission to perform this action.";
                    }
                    if (error.response.status === 404) {
                        return "Add-on not found. Please refresh the page.";
                    }
                }
                return error.message || "Failed to update add-on. Please try again.";
            },

            /**
             * Save add-on toggle state
             */
            saveAddons(addon_id, event, index) {
                if (this.loadingAddons[addon_id] || this.isSaving) return;

                const addon = this.addons[index];
                if (!addon) return;

                // Check if disabling Power BI - show confirmation modal
                const isPowerBI = addon.label && addon.label.toLowerCase().includes('power bi');
                const isDisabling = addon.is_checked && !event.value;

                if (isPowerBI && isDisabling) {
                    this.pendingPowerBIAction = { addon_id, event, index, addon };
                    this.showPowerBIModal = true;
                    return;
                }

                this.processAddonToggle(addon_id, event, index, addon, false);
            },
            
            /**
             * Close Power BI confirmation modal
             */
            closePowerBIModal() {
                if (this.pendingPowerBIAction) {
                    const { addon } = this.pendingPowerBIAction;
                    this.$set(addon, 'renderKey', (addon.renderKey || 0) + 1);
                }
                this.showPowerBIModal = false;
                this.pendingPowerBIAction = null;
            },

            /**
             * Handle Keep Data - just disable the addon
             */
            handleKeepData() {
                if (!this.pendingPowerBIAction) return;

                const { addon_id, event, index, addon } = this.pendingPowerBIAction;
                this.showPowerBIModal = false;
                this.pendingPowerBIAction = null;

                this.processAddonToggle(addon_id, event, index, addon, false);
            },

            /**
             * Handle Discard Data - disable addon and delete workspace
             */
            handleDiscardData() {
                if (!this.pendingPowerBIAction) return;

                const { addon_id, event, index, addon } = this.pendingPowerBIAction;
                this.showPowerBIModal = false;
                this.pendingPowerBIAction = null;

                this.processAddonToggle(addon_id, event, index, addon, true);
            },

            /**
             * Process addon toggle with optional workspace deletion
             */
            processAddonToggle(addon_id, event, index, addon, deleteWorkspace) {
                this.previousStates[addon_id] = addon.is_checked;
                this.$set(addon, 'is_checked', event.value);
                this.$set(this.loadingAddons, addon_id, true);
                this.isSaving = true;

                const formData = new FormData();
                formData.append('organization_id', this.org_id);
                formData.append('addon_id', addon_id);
                if (deleteWorkspace) {
                    formData.append('delete_workspace', '1');
                }

                const isPowerBI = addon.label && addon.label.toLowerCase().includes('power bi');
                const timeout = isPowerBI ? 300000 : 30000;

                const timeoutId = setTimeout(() => {
                    if (this.loadingAddons[addon_id]) {
                        this.revertToggle(addon_id);
                        this.$set(this.loadingAddons, addon_id, false);
                        this.isSaving = false;
                        this.$toast.warning("Request timed out. Please try again.");
                    }
                }, timeout);

                this.requestTimeouts[addon_id] = timeoutId;

                axios.post('/admin/organization/addons', formData, { timeout })
                        .then((response) => {
                        clearTimeout(timeoutId);
                        delete this.requestTimeouts[addon_id];
                        this.$toast.success(response.data.message || "Add-on updated successfully.");
                        this.refreshAddonState(addon_id);

                        // AI Assistant: load plans on enable, clear on disable
                        if (this.isAiAssistant(addon)) {
                            if (event.value) {
                                this.fetchPlans();
                            } else {
                                this.resetPlanState();
                            }
                        }
                        })
                        .catch((error) => {
                        clearTimeout(timeoutId);
                        delete this.requestTimeouts[addon_id];
                        this.revertToggle(addon_id);
                        this.refreshAddonState(addon_id);
                        this.$toast.error(this.getErrorMessage(error));
                        // eslint-disable-next-line no-console
                        console.error('Error saving add-on:', error);
                    })
                    .finally(() => {
                        this.$set(this.loadingAddons, addon_id, false);
                        this.isSaving = false;
                        delete this.previousStates[addon_id];
                        });
            },

            /**
             * Refresh addon state from server
             */
            refreshAddonState(addon_id) {
                axios.get(`admin/organization/addons/${this.org_id}`)
                    .then((response) => {
                        const res = response.data.data;
                        if (res && Array.isArray(res)) {
                            const updatedAddon = res.find(a => a.id === addon_id);
                            const localAddon = this.addons.find(a => a.id === addon_id);
                            if (updatedAddon && localAddon) {
                                this.$set(localAddon, 'is_checked', updatedAddon.is_checked);
                                this.$set(localAddon, 'renderKey', (localAddon.renderKey || 0) + 1);
                            }
                        }
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.warn('Failed to refresh addon state:', error);
                    });
            },

            /**
             * Identify the AI Assistant addon. Prefer the slug set by the
             * server, fall back to the label for safety.
             */
            isAiAssistant(addon) {
                if (!addon) return false;
                if (addon.slug) return addon.slug === 'ai_assistant';
                return !!(addon.label && addon.label.toLowerCase() === 'ai assistant');
            },

            resetPlanState() {
                this.plans = [];
                this.selectedPlanId = null;
                this.lastSavedPlanId = null;
                this.currentSubscription = null;
            },

            /**
             * Format a server date, tolerating nulls, MySQL zero-dates and
             * anything else moment can't parse.
             */
            formatDate(value) {
                if (!value) return '';

                const date = moment(String(value));
                return date.isValid() ? date.format('MMM DD, Y') : '';
            },

            /**
             * Price for a plan, or '' when the server didn't send a usable
             * one -- never render "$NaN/mo" at an admin.
             */
            formatPlanPrice(plan) {
                const raw = plan && plan.price_cents;
                if (raw === null || raw === undefined || raw === '') return '';

                const cents = Number(raw);
                if (isNaN(cents) || !isFinite(cents)) return '';

                const symbol = (plan && plan.currency_symbol) || '$';
                return `${symbol}${(cents / 100).toFixed(2)}/mo`;
            },

            /**
             * Option text for a plan. Falls back to the name alone when the
             * price is unusable, so the picker stays selectable either way.
             */
            planLabel(plan) {
                const name = (plan && plan.name) || 'Unnamed plan';
                const price = this.formatPlanPrice(plan);

                return price ? `${name} — ${price}` : name;
            },

            /**
             * Fetch AI plans + the org's current subscription.
             */
            fetchPlans() {
                this.isLoadingPlans = true;

                axios.get(`admin/organization/ai-plan/${this.org_id}`)
                    .then((response) => {
                        const data = response.data.data || {};
                        this.plans = data.plans || [];
                        this.currentSubscription = data.current_subscription || null;
                        this.selectedPlanId = this.currentSubscription
                            ? this.currentSubscription.ai_plan_id
                            : (this.plans[0] && this.plans[0].id);
                        this.lastSavedPlanId = this.selectedPlanId;
                    })
                    .catch(() => {
                        this.$toast.error('Failed to load AI plans. Please try again.');
                    })
                    .finally(() => {
                        this.isLoadingPlans = false;
                    });
            },

            /**
             * Persist the selected AI plan for the org.
             */
            savePlan() {
                if (!this.selectedPlanId) return;

                // v-model has already moved the select, so remember the last
                // accepted plan to fall back to if the request fails.
                const previousPlanId = this.lastSavedPlanId;
                this.isSavingPlan = true;

                axios.post('admin/organization/ai-plan', {
                    organization_id: this.org_id,
                    ai_plan_id: this.selectedPlanId,
                })
                    .then((response) => {
                        this.lastSavedPlanId = this.selectedPlanId;
                        this.$toast.success(response.data.message || 'Plan updated successfully.');
                        this.fetchPlans();
                    })
                    .catch((error) => {
                        this.selectedPlanId = previousPlanId;
                        const message = (error.response && error.response.data && error.response.data.message)
                            || 'Failed to update plan. Please try again.';
                        this.$toast.error(message);
                    })
                    .finally(() => {
                        this.isSavingPlan = false;
                    });
            }
        }
    }
</script>

<style scoped>
    .add-on-settings-container {
        min-height: 100px;
        position: relative;
    }

    /* Spinner Styles */
    .spinner-icon {
        font-size: 24px;
        color: rgb(20, 88, 144);
        animation: spin 1s linear infinite;
    }

    .spinner-icon-inline {
        font-size: 18px;
        color: rgb(20, 88, 144);
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    /* Toggle Button Styles */
    .vue-js-switch {
        border: 2px solid rgb(20, 88, 144);
        border-radius: 14px;
        position: relative;
        transition: opacity 0.3s ease;
    }

    .vue-js-switch.toggle-disabled {
        opacity: 0.6;
        cursor: not-allowed;
        pointer-events: none;
    }

    .v-switch-button {
        background-color: rgb(20, 88, 144) !important;
    }

    .v-switch-core {
        background-color: white !important;
    }

    /* Add-on Label Styles */
    p.add-on-keys {
        color: rgb(20, 88, 144) !important;
        font-size: 16px;
        font-weight: 500;
    }

    /* AI Plan Section (inline under the AI Assistant addon row) */
    .plan-section {
        padding-left: 0.5rem;
        border-left: 3px solid rgb(20, 88, 144);
        margin-top: 0.75rem;
    }

    .plan-section-title {
        color: rgb(20, 88, 144);
        font-weight: 600;
        margin-bottom: 0.5rem;
        font-size: 14px;
    }

    /* Form Group Spacing */
    .form-group {
        margin-bottom: 1.5rem;
        padding: 0.75rem 0;
        border-bottom: 1px solid #f0f0f0;
    }

    .form-group:last-child {
        border-bottom: none;
    }

    /* Empty State */
    .text-muted {
        color: #6c757d;
    }

    /* Power BI Message */
    .text-info {
        color: #17a2b8;
        font-style: italic;
    }

    /* Database Error State */
    .database-error-state {
        text-align: center;
        padding: 3rem 2rem;
        background: #fff9f2;
        border: 2px solid #ffeaa7;
        border-radius: 8px;
        margin: 1rem 0;
    }

    .error-icon-wrapper {
        margin-bottom: 1.5rem;
    }

    .error-icon {
        font-size: 48px;
        color: #f39c12;
    }

    .error-title {
        color: #333;
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 1rem;
    }

    .error-message {
        color: #555;
        font-size: 15px;
        line-height: 1.6;
        margin-bottom: 1.5rem;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;
    }

    .error-details {
        padding: 0.75rem 1.5rem;
        background: rgba(243, 156, 18, 0.1);
        border-radius: 6px;
        display: inline-block;
    }

    .error-details small {
        color: #666;
        font-size: 13px;
    }

    /* Power BI Modal Styles */
    .powerbi-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
    }

    .powerbi-modal-content {
        background: white;
        border-radius: 8px;
        width: 90%;
        max-width: 500px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        overflow: hidden;
    }

    .powerbi-modal-header {
        background: rgb(20, 88, 144);
        color: white;
        padding: 1.25rem 1.5rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .powerbi-modal-header h4 {
        margin: 0;
        font-size: 18px;
        font-weight: 500;
    }

    .close-btn {
        background: transparent;
        border: none;
        color: white;
        font-size: 20px;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background 0.2s ease;
    }

    .close-btn:hover {
        background: rgba(255, 255, 255, 0.1);
    }

    .powerbi-modal-body {
        padding: 2rem 1.5rem;
    }

    .modal-message {
        font-size: 16px;
        color: #333;
        margin-bottom: 1.5rem;
        text-align: center;
    }

    .modal-actions {
        display: flex;
        gap: 1rem;
        justify-content: center;
    }

    .action-btn {
        flex: 1;
        padding: 0.875rem 1.5rem;
        border: none;
        border-radius: 6px;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .keep-data-btn {
        background: rgb(20, 88, 144);
        color: white;
    }

    .keep-data-btn:hover {
        background: rgb(15, 70, 115);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(20, 88, 144, 0.3);
    }

    .discard-data-btn {
        background: #f8f9fa;
        color: #dc3545;
        border: 1px solid #dc3545;
    }

    .discard-data-btn:hover {
        background: #dc3545;
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
    }
</style>